/**
 * VERA Evidence Thumbnail Component
 * Small 3D preview canvas for evidence cards in the case view.
 * Renders a mini auto-rotating scene for 3D scan items (PLY/GLB),
 * and a styled icon placeholder for non-3D items.
 */
import React, { useRef, useState, useEffect, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  Cube24Regular,
  Image24Regular,
  Video24Regular,
  DocumentBulletList24Regular,
} from "@fluentui/react-icons";

import type { EvidenceFormat, EvidenceCategory } from "../types";

/** Sets the Three.js scene background to light grey */
const SceneSetup: React.FC = () => {
  const { scene } = useThree();
  useEffect(() => {
    scene.background = new THREE.Color("#f1f3f4");
  }, [scene]);
  return null;
};

// ── Mini rotating point cloud for PLY thumbnails ────────────

const MiniPointCloud: React.FC<{ url?: string; hasFile: boolean }> = ({
  url,
  hasFile,
}) => {
  const pointsRef = useRef<THREE.Points>(null);
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null);

  // Generate a procedural mini point cloud if no real file
  useEffect(() => {
    if (hasFile && url) {
      const loader = new PLYLoader();
      loader.load(
        url,
        (geo) => {
          geo.computeVertexNormals();
          geo.center();
          geo.computeBoundingBox();
          const box = geo.boundingBox;
          if (box) {
            const maxDim = Math.max(
              box.max.x - box.min.x,
              box.max.y - box.min.y,
              box.max.z - box.min.z
            );
            if (maxDim > 0) geo.scale(3 / maxDim, 3 / maxDim, 3 / maxDim);
          }
          setGeometry(geo);
        },
        undefined,
        () => setGeometry(createDemoGeo())
      );
    } else {
      setGeometry(createDemoGeo());
    }
  }, [url, hasFile]);

  useFrame((_, delta) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.3;
    }
  });

  if (!geometry) return null;

  const hasColors = !!geometry.attributes.color;

  return (
    <points ref={pointsRef} geometry={geometry}>
      <pointsMaterial
        size={0.04}
        vertexColors={hasColors}
        color={hasColors ? undefined : "#6ea8d9"}
        transparent
        opacity={0.85}
        sizeAttenuation
      />
    </points>
  );
};

// ── Mini rotating GLB model for thumbnails ──────────────────

const MiniGlbModel: React.FC<{ url?: string; hasFile: boolean }> = ({
  url,
  hasFile,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const [scene, setScene] = useState<THREE.Group | null>(null);

  useEffect(() => {
    if (hasFile && url) {
      const loader = new GLTFLoader();
      loader.load(
        url,
        (gltf) => {
          const s = gltf.scene;
          const box = new THREE.Box3().setFromObject(s);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          s.position.sub(center);
          if (maxDim > 0) s.scale.multiplyScalar(3 / maxDim);
          setScene(s);
        },
        undefined,
        () => setScene(null)
      );
    }
  }, [url, hasFile]);

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.3;
    }
  });

  if (!hasFile || !scene) {
    return <MiniDemoMesh />;
  }

  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
};

// ── Demo geometries ─────────────────────────────────────────

function createDemoGeo(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const count = 8000;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    const section = Math.random();
    if (section < 0.4) {
      // Floor
      positions[i3] = (Math.random() - 0.5) * 4;
      positions[i3 + 1] = Math.random() * 0.05;
      positions[i3 + 2] = (Math.random() - 0.5) * 4;
      colors[i3] = 0.2;
      colors[i3 + 1] = 0.22;
      colors[i3 + 2] = 0.25;
    } else if (section < 0.7) {
      // Walls
      const wall = Math.floor(Math.random() * 4);
      const h = Math.random() * 2;
      const spread = (Math.random() - 0.5) * 4;
      positions[i3] = wall < 2 ? (wall === 0 ? -2 : 2) : spread;
      positions[i3 + 1] = h;
      positions[i3 + 2] = wall >= 2 ? (wall === 2 ? -2 : 2) : spread;
      colors[i3] = 0.3;
      colors[i3 + 1] = 0.32;
      colors[i3 + 2] = 0.36;
    } else {
      // Objects
      const cx = (Math.random() - 0.5) * 2.5;
      const cz = (Math.random() - 0.5) * 2.5;
      positions[i3] = cx + (Math.random() - 0.5) * 0.6;
      positions[i3 + 1] = Math.random() * 0.8;
      positions[i3 + 2] = cz + (Math.random() - 0.5) * 0.6;
      colors[i3] = 0.0;
      colors[i3 + 1] = 0.2;
      colors[i3 + 2] = 0.45;
    }
  }

  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

const MiniDemoMesh: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.3;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh position={[0, 0.6, 0]}>
        <boxGeometry args={[1.4, 1.2, 1.2]} />
        <meshStandardMaterial color="#5a6a7a" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.45, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[1.1, 0.6, 4]} />
        <meshStandardMaterial color="#8b6540" roughness={0.8} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[3.5, 3.5]} />
        <meshStandardMaterial color="#4a5a3a" roughness={0.9} />
      </mesh>
    </group>
  );
};

// ── Non-3D icon placeholder ─────────────────────────────────

const categoryIcons: Record<EvidenceCategory, React.ReactNode> = {
  "3d-scan": <Cube24Regular />,
  photograph: <Image24Regular />,
  video: <Video24Regular />,
  document: <DocumentBulletList24Regular />,
  audio: <DocumentBulletList24Regular />,
};

/** Icon accent colors on the light grey background */
const categoryIconColors: Record<EvidenceCategory, string> = {
  "3d-scan": "#004C97",
  photograph: "#0e7a0d",
  video: "#8b5cf6",
  document: "#63666A",
  audio: "#e67e22",
};

// ── Main Thumbnail Component ────────────────────────────────

interface EvidenceThumbnailProps {
  category: EvidenceCategory;
  format: EvidenceFormat;
  fileUrl: string;
  hasRealFile?: boolean;
  height?: number;
}

export const EvidenceThumbnail: React.FC<EvidenceThumbnailProps> = ({
  category,
  format,
  fileUrl,
  hasRealFile = false,
  height = 160,
}) => {
  const is3D = category === "3d-scan" && (format === "ply" || format === "glb");

  if (!is3D) {
    // Non-3D: light grey background with tinted icon
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f1f3f4",
          position: "relative",
        }}
      >
        <div
          style={{
            fontSize: "48px",
            color: categoryIconColors[category],
            opacity: 0.35,
          }}
        >
          {categoryIcons[category]}
        </div>
      </div>
    );
  }

  // 3D: render a live mini canvas
  return (
    <div
      style={{
        height,
        background: "#f1f3f4",
        position: "relative",
        cursor: "pointer",
      }}
    >
      <Canvas
        gl={{ antialias: true, alpha: false, powerPreference: "low-power" }}
        camera={{ position: [4, 3, 4], fov: 45 }}
        dpr={[1, 1.5]}
      >
        <SceneSetup />
        <ambientLight intensity={format === "glb" ? 0.6 : 0.5} />
        <directionalLight position={[5, 5, 3]} intensity={format === "glb" ? 1.0 : 0.7} />
        <directionalLight position={[-3, 3, -3]} intensity={0.3} />

        <Suspense fallback={null}>
          {format === "glb" ? (
            <MiniGlbModel url={fileUrl} hasFile={hasRealFile} />
          ) : (
            <MiniPointCloud url={fileUrl} hasFile={hasRealFile} />
          )}
        </Suspense>
      </Canvas>

      {/* Subtle "3D" indicator */}
      <div
        style={{
          position: "absolute",
          bottom: 6,
          left: 6,
          fontSize: "10px",
          fontWeight: 600,
          color: "#8a8d91",
          fontFamily: "'Poppins', sans-serif",
          letterSpacing: "0.5px",
          display: "flex",
          alignItems: "center",
          gap: "3px",
        }}
      >
        <Cube24Regular style={{ fontSize: "12px" }} />
        INTERACTIVE 3D
      </div>
    </div>
  );
};

export default EvidenceThumbnail;
